/**
 * Contact people grid with client-side filtering by location and function.
 *
 * Markup contract:
 * .contact-people-grid
 *   form.contact-people-grid__filters
 *     select[name="location"]
 *     select[name="function"]
 *   p.contact-people-grid__status[aria-live="polite"]
 *   div.contact-people-grid__groups
 *     section.contact-people-grid__group
 *       h2.contact-people-grid__group-title
 *       div.contact-people-grid__grid
 *         article.contact-people-grid__card[data-location][data-function]
 *
 * @module contact-people-grid
 */

const DEFAULT_STATUS_TEMPLATE = '{count} results found.';

/**
 * Normalizes filter values for case-insensitive comparisons.
 *
 * @param {string | null | undefined} value - Raw value.
 * @returns {string}
 */
function normalizeValue(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * Returns normalized card tags from a card data attribute.
 *
 * @param {HTMLElement} card - Card element.
 * @param {'location' | 'function'} key - Data key.
 * @returns {string[]}
 */
function getCardTags(card, key) {
  const rawValue = card.dataset[key] || '';

  return rawValue
    .split(',')
    .map((item) => normalizeValue(item))
    .filter(Boolean);
}

/**
 * Checks whether card tags match an active filter value.
 *
 * @param {string[]} tags - Normalized card tags.
 * @param {string} selectedValue - Normalized selected value.
 * @returns {boolean}
 */
function matchesFilter(tags, selectedValue) {
  if (!selectedValue || selectedValue === 'all') return true;
  return tags.includes(selectedValue);
}

/**
 * Formats the status text message.
 *
 * @param {string} template - Message template.
 * @param {number} count - Visible card count.
 * @returns {string}
 */
function formatStatus(template, count) {
  return template.replaceAll('{count}', String(count));
}

/**
 * Applies current filters to cards and group wrappers.
 *
 * @param {HTMLElement} root - Component root.
 * @returns {void}
 */
function applyFilters(root) {
  const locationSelect = root.querySelector('select[name="location"]');
  const functionSelect = root.querySelector('select[name="function"]');
  const status = root.querySelector('.contact-people-grid__status');
  const groups = Array.from(root.querySelectorAll('.contact-people-grid__group'));

  const selectedLocation = locationSelect instanceof HTMLSelectElement
    ? normalizeValue(locationSelect.value)
    : 'all';
  const selectedFunction = functionSelect instanceof HTMLSelectElement
    ? normalizeValue(functionSelect.value)
    : 'all';

  let visibleCount = 0;

  groups.forEach((group) => {
    const cards = Array.from(group.querySelectorAll('.contact-people-grid__card'));
    let groupVisibleCount = 0;

    cards.forEach((card) => {
      const locationTags = getCardTags(card, 'location');
      const functionTags = getCardTags(card, 'function');

      const isVisible = matchesFilter(locationTags, selectedLocation)
        && matchesFilter(functionTags, selectedFunction);

      card.hidden = !isVisible;
      card.setAttribute('aria-hidden', String(!isVisible));

      if (isVisible) {
        groupVisibleCount += 1;
        visibleCount += 1;
      }
    });

    group.hidden = groupVisibleCount === 0;
  });

  if (status instanceof HTMLElement) {
    const template = root.dataset.statusTemplate || DEFAULT_STATUS_TEMPLATE;
    status.textContent = formatStatus(template, visibleCount);
  }
}

/**
 * Initializes one contact people grid root element.
 *
 * @param {HTMLElement} root - Component root element.
 * @returns {void}
 */
function initContactPeopleGridSection(root) {
  if (root.dataset.contactPeopleGridInitialized === 'true') return;

  const filters = root.querySelector('.contact-people-grid__filters');
  if (!(filters instanceof HTMLFormElement)) return;

  root.dataset.contactPeopleGridInitialized = 'true';

  const selects = Array.from(filters.querySelectorAll('select'));

  selects.forEach((select) => {
    select.addEventListener('change', () => {
      applyFilters(root);
    });
  });

  applyFilters(root);
}

/**
 * Initializes all contact people grid sections.
 *
 * @returns {void}
 */
export function initContactPeopleGrid() {
  const sections = document.querySelectorAll('.contact-people-grid');

  sections.forEach((section) => {
    if (section instanceof HTMLElement) {
      initContactPeopleGridSection(section);
    }
  });
}
